import { createFileRoute, Link } from "@tanstack/react-router";

import {
  ExternalAuthorityLink,
  LegalCallout,
  LegalDocument,
  LegalSection,
} from "@/features/legal/LegalDocument";
import { LEGAL_RESPONSIBLE } from "@/features/legal/legal-config";

export const Route = createFileRoute("/legal/terminos")({
  head: () => ({
    meta: [
      { title: "Términos de uso y de compra | Socrattica" },
      {
        name: "description",
        content:
          "Condiciones de uso del sitio y de compra de guías y recursos digitales en Socrattica.",
      },
    ],
  }),
  component: TermsPage,
});

function TermsPage() {
  return (
    <LegalDocument
      eyebrow="Términos de uso y de compra"
      title="Reglas claras para usar, elegir y comprar."
      summary="Estas condiciones regulan el acceso a Socrattica y las compras de guías y recursos digitales. Consúltalas antes y después de cualquier transacción."
    >
      <LegalCallout title="Quién ofrece los productos">
        <p>
          Los productos se ofrecen bajo el nombre Socrattica por{" "}
          <strong>{LEGAL_RESPONSIBLE.name}</strong>, persona natural domiciliada en Colombia.
          Contacto: <a href={`mailto:${LEGAL_RESPONSIBLE.email}`}>{LEGAL_RESPONSIBLE.email}</a>.
        </p>
      </LegalCallout>

      <LegalSection id="aceptacion" title="1. Aceptación y capacidad">
        <p>
          Al navegar puedes consultar información pública. Al crear una cuenta, suscribirte,
          descargar o confirmar una compra aceptas las condiciones aplicables a esa acción. Si no
          estás de acuerdo, no completes la acción.
        </p>
        <p>
          Debes tener capacidad legal para contratar. Las personas menores de edad deben actuar con
          autorización y acompañamiento de su representante.
        </p>
      </LegalSection>

      <LegalSection id="servicio" title="2. Qué ofrece Socrattica">
        <p>
          Socrattica es una librería digital y un pódcast educativo para aprender inglés. La tienda
          ofrece productos descargables que pueden incluir PDF, EPUB, audio, ejercicios o
          plantillas, según la ficha de cada producto.
        </p>
        <p>
          Cada ficha debe indicar título, autor, nivel, formato, idioma, contenido incluido, precio,
          moneda, disponibilidad y otras restricciones relevantes. Una muestra, imagen o descripción
          ilustra el producto, pero el alcance contractual es el informado en la ficha y el resumen
          final del pedido.
        </p>
      </LegalSection>

      <LegalSection id="cuenta" title="3. Cuenta y correo de compra">
        <p>
          Debes suministrar información exacta y mantener segura tu contraseña. El correo
          verificable permite vincular pedidos con tu cuenta, recuperar acceso, consultar
          comprobantes y descargar archivos. Eres responsable de la actividad realizada con tus
          credenciales, salvo uso no autorizado que nos reportes oportunamente.
        </p>
        <p>
          Podemos suspender temporalmente una cuenta por riesgo de fraude, incumplimiento de la
          licencia, acceso automatizado abusivo o amenaza a la seguridad, procurando informarte y
          permitir una aclaración.
        </p>
      </LegalSection>

      <LegalSection id="compra" title="4. Formación de la compra">
        <ol className="ml-5 list-decimal space-y-2">
          <li>Seleccionas los recursos y revisas el carrito.</li>
          <li>Confirmas correo, productos, precio individual, total y medio de pago.</li>
          <li>Aceptas estos términos y autorizas, si lo deseas, la entrega digital inmediata.</li>
          <li>La pasarela procesa el pago y comunica su resultado.</li>
          <li>
            La compra se perfecciona cuando Socrattica recibe la aprobación y genera la confirmación
            o el pedido. Un estado “pendiente” no acredita pago.
          </li>
        </ol>
        <p>
          Antes de pagar puedes corregir cantidades o retirar productos. Conserva el número de
          pedido y el comprobante.
        </p>
      </LegalSection>

      <LegalSection id="precio" title="5. Precios, moneda e impuestos">
        <p>
          El precio exigible es el total mostrado en el resumen final. Los pagos se procesan en
          pesos colombianos (COP), salvo que el checkout indique expresamente otra moneda. Los
          valores en USD u otras monedas son una referencia estimada y la entidad financiera puede
          aplicar su propia tasa o cargos.
        </p>
        <p>
          El total incluirá los impuestos o costos obligatorios aplicables. No hay envío físico para
          productos exclusivamente digitales. Una promoción rige durante el plazo y disponibilidad
          informados.
        </p>
      </LegalSection>

      <LegalSection id="pago" title="6. Pago y seguridad">
        <p>
          El checkout puede remitir a ePayco o a otra pasarela claramente identificada. La pasarela,
          el banco y otros participantes aplican sus propias verificaciones y condiciones.
          Socrattica no recibe el número completo ni el código de seguridad de tu tarjeta.
        </p>
        <p>
          Un pago puede ser rechazado o quedar pendiente por controles del proveedor. No intentes
          múltiples pagos si no conoces el estado; primero revisa tu cuenta o solicita soporte.
        </p>
      </LegalSection>

      <LegalSection id="entrega" title="7. Entrega digital">
        <p>
          Una vez confirmado el pago, el recurso se vincula al correo o cuenta de la compra y se
          habilita mediante un botón privado de descarga. Normalmente ocurre en minutos. El botón
          permanece disponible desde la cuenta mientras la compra siga aprobada. Puedes volver a
          descargar la versión adquirida sin realizar una nueva compra.
        </p>
        <p>
          Un reembolso, contracargo o anulación válida deshabilita nuevas descargas. Si el producto
          deja de venderse, procuraremos conservar el acceso a la versión comprada o brindar una
          solución equivalente. Te recomendamos guardar una copia local y un respaldo privado.
        </p>
        <p>
          Si el acceso no aparece dentro de 24 horas o si el archivo es defectuoso, crea un{" "}
          <Link to="/productos/tickets">ticket de soporte</Link> con el número de pedido. La
          disponibilidad en la cuenta no limita la garantía, la solución por falta de entrega ni
          otros derechos irrenunciables.
        </p>
      </LegalSection>

      <LegalSection id="licencia" title="8. Descarga, licencia y propiedad intelectual">
        <p>
          La compra no transfiere derechos de autor. Recibes una licencia personal, no exclusiva y
          no transferible para usar el archivo de acuerdo con las{" "}
          <Link to="/legal/licencia">condiciones de descarga y licencia</Link>. No puedes revender,
          compartir públicamente, publicar, sublicenciar ni eliminar avisos de autoría.
        </p>
      </LegalSection>

      <LegalSection id="retracto" title="9. Retracto, garantía, reversión y reembolsos">
        <p>
          Tus derechos no se eliminan por tratarse de un archivo digital. Los casos, requisitos,
          plazos y canales están explicados en la{" "}
          <Link to="/legal/reembolsos">política de reembolsos</Link>. Cuando solicites entrega
          inmediata y el acceso o descarga comience con tu autorización, esa circunstancia puede
          afectar la procedencia del retracto en los casos permitidos por la ley, pero no limita la
          garantía por un archivo defectuoso ni otros derechos irrenunciables.
        </p>
      </LegalSection>

      <LegalSection id="uso" title="10. Uso permitido del sitio">
        <p>No puedes:</p>
        <ul>
          <li>Infringir derechos de autor, marcas o derechos de terceros.</li>
          <li>Compartir credenciales o eludir controles de acceso y descarga.</li>
          <li>
            Introducir código malicioso, interferir con el servicio o probar vulnerabilidades.
          </li>
          <li>Extraer de forma automatizada el catálogo, cuentas o contenidos sin autorización.</li>
          <li>Usar el sitio para fraude, suplantación o actividades ilegales.</li>
        </ul>
      </LegalSection>

      <LegalSection id="comunidad" title="11. Círculos de los episodios">
        <p>
          Las notas del Laboratorio son privadas y permanecen en tu navegador. Publicar una
          reflexión en un círculo es una acción separada y voluntaria. Conservas la autoría de tu
          aporte y otorgas a Socrattica una autorización no exclusiva para alojarlo, mostrarlo y
          moderarlo dentro del servicio mientras permanezca publicado.
        </p>
        <p>
          Solo puedes publicar contenido propio y respetuoso. No incluyas información sensible,
          datos personales de terceros, publicidad, enlaces engañosos, amenazas, acoso, contenido
          discriminatorio ni material que infrinja derechos. Podemos ocultar o eliminar aportes,
          limitar interacciones o suspender la participación cuando exista un reporte razonable,
          riesgo para otras personas, abuso técnico o incumplimiento de estas reglas.
        </p>
        <p>
          Cada cuenta puede mantener una reflexión principal por episodio, editarla o eliminarla.
          Las reacciones “Me hizo pensar” no representan aprobación de Socrattica. Los reportes
          deben usarse de buena fe y no como mecanismo para silenciar opiniones legítimas.
        </p>
      </LegalSection>

      <LegalSection id="educativo" title="12. Alcance educativo">
        <p>
          Los materiales apoyan el aprendizaje, pero no garantizan por sí solos un nivel, resultado,
          empleo, certificación o plazo específico. El progreso depende de práctica, contexto y
          otros factores. Las recomendaciones del pódcast son informativas y no sustituyen asesoría
          profesional.
        </p>
      </LegalSection>

      <LegalSection id="disponibilidad" title="13. Disponibilidad y cambios">
        <p>
          Podemos hacer mantenimiento, corregir errores o cambiar funciones. Procuraremos conservar
          el acceso legítimo a compras pagadas y ofrecer una alternativa razonable si una migración
          técnica lo afecta. Los cambios sustanciales a una compra ya perfeccionada no se aplicarán
          retroactivamente en perjuicio del consumidor.
        </p>
      </LegalSection>

      <LegalSection id="responsabilidad" title="14. Responsabilidad">
        <p>
          Respondemos por la calidad, idoneidad y conformidad exigibles del producto y por nuestras
          obligaciones legales. No respondemos por fallas atribuibles exclusivamente al dispositivo,
          software incompatible no informado, conexión del usuario, uso contrario a instrucciones o
          servicios de terceros fuera de nuestro control, sin perjuicio de las responsabilidades que
          legalmente correspondan.
        </p>
      </LegalSection>

      <LegalSection id="reclamos" title="15. Soporte y reclamaciones">
        <p>
          Puedes escribir a{" "}
          <a href={`mailto:${LEGAL_RESPONSIBLE.email}`}>{LEGAL_RESPONSIBLE.email}</a> o usar el{" "}
          <Link to="/productos/tickets">centro de soporte</Link>, que genera referencia, fecha, hora
          y seguimiento. Nuestro objetivo de primera respuesta es de{" "}
          {LEGAL_RESPONSIBLE.responseTime}.
        </p>
        <p>
          También puedes consultar la información y los canales de la{" "}
          <ExternalAuthorityLink href="https://www.sic.gov.co/">
            Superintendencia de Industria y Comercio
          </ExternalAuthorityLink>
          , autoridad colombiana de protección al consumidor.
        </p>
      </LegalSection>

      <LegalSection id="ley" title="16. Ley aplicable y solución de controversias">
        <p>
          Se aplica la ley colombiana. Intentaremos resolver cualquier diferencia directamente por
          los canales de soporte. Nada en estos términos restringe el derecho del consumidor a
          acudir a la SIC, a un juez o a la autoridad competente, ni impone una renuncia a derechos
          irrenunciables.
        </p>
      </LegalSection>

      <LegalSection id="cambios" title="17. Cambios y versión aplicable">
        <p>
          Podemos actualizar estas condiciones para futuras operaciones. La compra se rige por la
          versión aceptada al confirmarla, salvo cambios legales obligatorios o condiciones
          posteriores más favorables. Esta página permanece disponible para consulta, impresión y
          descarga mediante las funciones del navegador.
        </p>
      </LegalSection>
    </LegalDocument>
  );
}
